import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { dateToEpoch, dateWordShort } from "helper/helper";

const PERIODS = ["one", "two", "three"];
const DAY_MS = 24 * 60 * 60 * 1000;

/** Worked slots of a day; "00:00 > 00:00" is how the planning marks a day off. */
const slotsOf = (periods) =>
    PERIODS.map((rank) => ({ start: periods?.[`periode_${rank}_start`], end: periods?.[`periode_${rank}_end`] })).filter(
        (slot) => slot.start && slot.end && !(slot.start === "00:00" && slot.end === "00:00")
    );

const signature = (day) => day.slots.map((slot) => `${slot.start}>${slot.end}`).join("|");

/** Consecutive days with the same hours share a line: [[mon, tue, wed], [thu], …]. */
const groupDays = (days) => {
    const groups = [];
    for (const day of days) {
        const group = groups[groups.length - 1];
        const previous = group?.[group.length - 1];
        const consecutive = previous && Math.round((dateToEpoch(day.date) - dateToEpoch(previous.date)) / DAY_MS) === 1;
        if (consecutive && signature(previous) === signature(day)) group.push(day);
        else groups.push([day]);
    }
    return groups;
};

/** "L", "M", "M", "J"… in Paris time, as the dates of the planning are. */
const dayInitial = (date) =>
    new Date(date).toLocaleDateString("fr-FR", { weekday: "narrow", timeZone: "Europe/Paris" }).toUpperCase();

/**
 * The mission planning, one week at a time, as in the temp worker app (MissionPlanning): days in
 * a row with the same hours share a line, days off stay visible in grey. It opens on the current
 * week, else the next one, else the first.
 *
 * `weeks`: the schedule of the mission (`schedule/mission/:id`), `{ days: [{ date, periods }] }`.
 */
const MissionPlanning = ({ weeks: scheduleWeeks, labels }) => {
    const weeks = useMemo(
        () =>
            [...scheduleWeeks]
                .sort((a, b) => a.week_number - b.week_number)
                .map((week) => ({
                    days: [...(week.days ?? [])]
                        .sort((a, b) => dateToEpoch(a.date) - dateToEpoch(b.date))
                        .map((day) => ({ date: day.date, slots: slotsOf(day.periods) })),
                }))
                .filter((week) => week.days.length),
        [scheduleWeeks]
    );
    const [index, setIndex] = useState(() => {
        const today = dateToEpoch(new Date());
        const current = weeks.findIndex((week) => dateToEpoch(week.days[week.days.length - 1].date) >= today);
        return current === -1 ? 0 : current;
    });

    if (!weeks.length) return null;

    const current = weeks[Math.min(index, weeks.length - 1)];
    const first = dateWordShort(current.days[0].date);
    const last = dateWordShort(current.days[current.days.length - 1].date);

    return (
        <div>
            <div className="mb-4 flex items-center gap-3">
                <div className="flex flex-1 flex-col gap-1">
                    <h2 className="text-sm font-semibold leading-5 text-gray-900">{labels.title}</h2>
                    <p className="text-sm leading-5 text-gray-500">
                        {first === last
                            ? labels.day.replace("{date}", first)
                            : labels.range.replace("{start}", first).replace("{end}", last)}
                    </p>
                </div>
                {weeks.length > 1 ? (
                    <div className="flex gap-2">
                        <WeekButton
                            icon={ChevronLeft}
                            label={labels.previous}
                            disabled={index === 0}
                            onClick={() => setIndex((value) => Math.max(0, value - 1))}
                        />
                        <WeekButton
                            icon={ChevronRight}
                            label={labels.next}
                            disabled={index >= weeks.length - 1}
                            onClick={() => setIndex((value) => Math.min(weeks.length - 1, value + 1))}
                        />
                    </div>
                ) : null}
            </div>

            {groupDays(current.days).map((group, position) => (
                <div key={group[0].date}>
                    {position > 0 ? <div className="my-2 border-t border-dashed border-gray-100" /> : null}
                    <div className="flex items-center py-3">
                        <div className="flex flex-[2] flex-wrap gap-2">
                            {group.map((day) => (
                                <div
                                    key={day.date}
                                    title={dateWordShort(day.date)}
                                    className="flex h-[52px] w-[52px] items-center justify-center rounded-full border border-gray-200 bg-white text-sm font-medium text-gray-900"
                                >
                                    {dayInitial(day.date)}
                                </div>
                            ))}
                        </div>
                        <div className="flex flex-[3] flex-col items-end gap-1 pl-4">
                            {group[0].slots.length ? (
                                group[0].slots.map((slot) => (
                                    <span key={`${slot.start}-${slot.end}`} className="badge badge-lg bg-yellow-50 text-yellow-700">
                                        {`${slot.start} > ${slot.end}`}
                                    </span>
                                ))
                            ) : (
                                <span className="badge badge-lg bg-gray-100 text-gray-700">{labels.notWorked}</span>
                            )}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

/** Bordered chevron of the week navigation. */
const WeekButton = ({ icon: Icon, label, disabled, onClick }) => (
    <button
        type="button"
        aria-label={label}
        title={label}
        disabled={disabled}
        onClick={onClick}
        className="corner-smooth-8 flex h-[42px] w-[42px] items-center justify-center border border-gray-100 bg-white enabled:hover:bg-gray-50 enabled:active:bg-gray-100"
    >
        <Icon size={24} strokeWidth={2} color={disabled ? "#9ca3af" : "#374151"} aria-hidden="true" />
    </button>
);

export default MissionPlanning;
